import React from 'react';
import { useSelector } from 'react-redux';

import { currentOpenChatSelector } from '../../../../../selectors';
import { useMessageContext } from '../../../../../utils/messageContext';
import { getFileSrc } from '../../../../../utils/getFileSrc';
import { formatBytes } from '../../../../../utils/formatBytes';
import { ChatImageContainer } from '../../../../ChatImageContainer';
import { ChatImageDraftContainer } from '../../../../ChatImageDraftContainer';

interface IUseImageAsDocumentResult {
  imageNode: React.ReactNode;
  src: string | null;
  name: string;
  size: string | undefined;
}

export const useImageAsDocument = (): IUseImageAsDocumentResult | null => {
  const {
    messageId,
    message: { files, status },
    chatId,
  } = useMessageContext();
  const imageAsDocument = files?.imagesAsDocuments?.[0];

  const { pendingFiles } = useSelector(currentOpenChatSelector) || {};

  return React.useMemo(() => {
    if (imageAsDocument) {
      const { uid, name, size } = imageAsDocument;

      return {
        imageNode: <ChatImageContainer image={imageAsDocument} withSave />,
        src: getFileSrc(uid, chatId),
        name,
        size: size ? formatBytes(size) : undefined,
      };
    }

    const blobFiles = pendingFiles?.[messageId]?.blobFiles || [];
    const imageAsDocumentBlobFiles = blobFiles.filter(({ type }) => type === 'imageAsDocument');

    if (imageAsDocumentBlobFiles.length) {
      const { file, cancellationToken } = imageAsDocumentBlobFiles[0];
      const { name, size } = file;

      return {
        imageNode: (
          <ChatImageDraftContainer
            file={file}
            messageId={messageId}
            cancellationToken={cancellationToken}
            messageStatus={status}
          />
        ),
        src: null,
        name,
        size: formatBytes(size),
      };
    }

    return null;
  }, [imageAsDocument, chatId, messageId, status, pendingFiles]);
};
